import {assertAndContinue} from 'Asserts';
import {getDataTableName} from 'notebook/data-tables/DataTableUtil';
import {hasNLO, isStructuredTable} from 'notebook/TypePredicates';
import {isNotePartWithItem, NoteLinkedObjectType, NotePart} from 'notebook/util/NoteUtilConstants';
import {NoteLinkedAssayData} from 'runs/AttachmentConstants';

import {UnknownNoteLinkedGroup} from '../groups/NoteLinkedGroupConstants';

/**
 * Tables that are in the shared note's spreadsheet context require a unique name so they can be
 * uniquely identified in formula references, e.g. `=Table1!A1`. These are automatically
 * detected as the type is registered in NoteEditorContainer.spreadsheetTypes.
 *
 * The below enumerated types are not currenty used in this shared spreadsheet context, but by
 * ensuring they are uniquely named, they can be safely enabled in the future without worrying
 * about retroactive naming collisions.
 *
 * This is also a good convention regardless as it allows us to ensure that every named item has
 * a user-facing identifier.
 */
export default function getExtraUsedNamesFromNotePart(notePart: NotePart): Array<string> {
  if (!hasNLO(notePart)) {
    return [];
  }
  switch (notePart.item.type) {
    // NoteLinkedAssayData has embedded, readonly tables.
    case NoteLinkedObjectType.NOTE_LINKED_ASSAY_DATA:
      return Array.from(iterUsedNamesFromNoteLinkedAssayData(notePart.item as NoteLinkedAssayData));

    // NoteLinkedGroups currently have an isolated evaluation context of just structured tables,
    // with the hopes of one day moving these into the shared evaluation context, so we enumerate
    // them manually here for now.
    case NoteLinkedObjectType.NOTE_LINKED_GROUP:
      return Array.from(iterUsedNamesFromGroup(notePart.item as UnknownNoteLinkedGroup));

    default:
      return [];
  }
}

function* iterUsedNamesFromNoteLinkedAssayData(noteLinkedAssayData: NoteLinkedAssayData): Generator<string> {
  for (const dataTable of noteLinkedAssayData.dataTables) {
    yield getDataTableName(dataTable);
  }
  for (const runOutputSamplesTable of noteLinkedAssayData.versionedData.structuredTables) {
    yield runOutputSamplesTable.name;
  }
}

function* iterUsedNamesFromGroup(group: UnknownNoteLinkedGroup): Generator<string> {
  yield group.name;
  for (const notePart of group.containedNoteParts) {
    if (!isNotePartWithItem(notePart) || !isStructuredTable(notePart.item)) {
      assertAndContinue(false, 'Expected all containedNoteParts to have a structured table', {
        kind: group.kind,
        notePartType: notePart.type,
        // @ts-expect-error
        nloType: notePart.item?.type,
      });
      continue;
    }
    yield notePart.item.name;
  }
}
